// UserCellValue.jsx

import PropTypes from "prop-types";
import { forwardRef } from "react";
import { useIntl } from "react-intl";
import Avatar from "@mui/material/Avatar";
import CardHeader from "@mui/material/CardHeader";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/styles";

const baseUserCardStyles = {
  width: "100%",
  padding: 0,
  whiteSpace: "nowrap",
  cursor: "default",
  "& .MuiCardHeader-avatar": {
    marginRight: "5px",
  },
  "& .MuiCardHeader-avatar, & .MuiAvatar-root, & .MuiAvatar-img": {
    cursor: "inherit",
  },
  "& .MuiCardHeader-content": {
    cursor: "inherit",
    overflow: "auto",
  },
  "& .MuiCardHeader-title": {
    cursor: "inherit",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "12px",
    lineHeight: "13px",
    paddingBottom: "1px",
  },
};

const StyledAvatar = styled(Avatar)({
  height: 27,
  width: 27,
  fontSize: "inherit",
});

const ProjectUserCard = forwardRef(({ avatar, disabled, name, onClick, ...rest }, ref) => (
  <CardHeader
    {...rest}
    avatar={avatar}
    disabled={disabled}
    onClick={onClick}
    ref={ref}
    title={name}
    sx={{
      ...baseUserCardStyles,
      ...Boolean(!disabled) && { cursor: "pointer" },
    }}
  />
));

const ProjectUserItem = ({ avatar, disabled, name, onClick, tooltip }) => (
  <Tooltip
    arrow
    describeChild={false}
    placement="left"
    title={tooltip}
  >
    <ProjectUserCard
      avatar={avatar}
      disabled={disabled}
      name={name}
      onClick={onClick}
    />
  </Tooltip>
);

const UserCellValue = ({ disabled, hideUnassigned, onClick, user }) => {
  const intl = useIntl();

  if (hideUnassigned && !user) {
    return;
  }

  if (!user) {
    const unassignedMessage = intl.formatMessage({ id: "project.userSelector.unassigned" });
    return (
      <ProjectUserItem
        avatar={<StyledAvatar>?</StyledAvatar>}
        disabled={disabled}
        name={unassignedMessage}
        onClick={onClick}
        tooltip={unassignedMessage}
      />
    );
  }

  const { avatar, firstname, lastname, username } = user;

  const initials = (firstname || "")[0] + (lastname || "")[0];

  const renderName = (separator) => {
    const separate = !!firstname && !!lastname;
    return <>{firstname}{separate && separator}{lastname}</>;
  };

  const renderTooltip = () => {
    const name = renderName(" ");
    return <>{name} &lt;{username}&gt;</>;
  };

  return (
    <ProjectUserItem
      avatar={<StyledAvatar src={avatar}>{initials}</StyledAvatar>}
      disabled={disabled}
      name={renderName(<br />)}
      onClick={onClick}
      tooltip={renderTooltip()}
    />
  );
};

UserCellValue.propTypes = {
  disabled: PropTypes.bool,
  hideUnassigned: PropTypes.bool,
  onClick: PropTypes.func,
  user: PropTypes.shape({
    avatar: PropTypes.string,
    firstname: PropTypes.string,
    lastname: PropTypes.string,
    username: PropTypes.string,
  }),
};

UserCellValue.displayName = "UserCellValue";

export default UserCellValue;
